import { jsonDateParser } from 'json-date-parser'

export interface RequestOptions<T> {
    method: 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH'
    url: string
    body?: T
    headers?: { [key: string]: string }
}

export function request<T, U>(
    requestOptions: RequestOptions<T>,
    onProgress?: (bytesUploaded: number) => void
): Promise<{
    body: U
    headers: { [key: string]: string }
    status: number
}> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        const { method, url, body, headers } = requestOptions
        xhr.open(method, url, true)

        Object.keys(headers).forEach((key) => xhr.setRequestHeader(key, headers[key]))

        xhr.onload = () => {
            if (xhr.status < 200 || xhr.status >= 300) {
                return reject()
            }
            resolve({
                headers: parseHeaders(xhr.getAllResponseHeaders()),
                body: xhr.response && JSON.parse(xhr.response, jsonDateParser),
                status: xhr.status,
            })
        }

        xhr.onerror = () => {
            reject()
        }

        if (xhr.upload && onProgress) {
            xhr.upload.onprogress = (event: ProgressEvent) => {
                if (!event.lengthComputable) return // Don't call on progress if the length is not computable

                onProgress(event.loaded)
            }
        }

        if (body) {
            const content = body instanceof ArrayBuffer ? (body as ArrayBuffer) : JSON.stringify(body)
            xhr.send(content)
        } else {
            xhr.send()
        }
    })
}

const parseHeaders = (headerString: string) => {
    // The input is a big block with multiple lines
    // First we split this string by line ending to get an array of
    // individual header strings
    const headerRows = headerString.trim().split(/[\r\n]+/)

    // Contruct a map of header key/value pairs
    return headerRows.reduce((output, headerRow) => {
        const [key, value] = headerRow.split(':')
        return {
            ...output,
            [key.trim().toLowerCase()]: value.trim(),
        }
    }, {})
}
